import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BrushCleaning, Calendar, ChevronLeft, ChevronRight, ClipboardList, Droplets,
  FileSignature, Loader2, Settings, Thermometer,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../context/AuthContext";
import DocumentCard from "./DocumentCard";
import GeneraMeseWizard from "./GeneraMeseWizard";
import PrintPreviewModal from "./PrintPreviewModal";
import HaccpConfigDialog from "./HaccpConfigDialog";
import RegistriDialog from "./RegistriDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATO_COLORS = {
  da_stampare: "bg-amber-100 text-amber-800 border-amber-200",
  stampato: "bg-blue-100 text-blue-800 border-blue-200",
  archiviato: "bg-purple-100 text-purple-800 border-purple-200",
  compilato: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const STATO_LABELS = {
  da_stampare: "Da stampare",
  stampato: "Stampato",
  archiviato: "Archiviato",
  compilato: "Compilato",
};

const ICONS = {
  fogli_firma: FileSignature,
  haccp: Thermometer,
  haccp_pulizie: BrushCleaning,
  schema_alvo: ClipboardList,
  schema_docce: Droplets,
  altri_registri: Calendar,
};

const ICON_WRAP = {
  fogli_firma: "bg-blue-50 text-blue-600 ring-blue-100",
  haccp: "bg-orange-50 text-orange-600 ring-orange-100",
  haccp_pulizie: "bg-teal-50 text-teal-600 ring-teal-100",
  schema_alvo: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  schema_docce: "bg-cyan-50 text-cyan-600 ring-cyan-100",
  altri_registri: "bg-violet-50 text-violet-600 ring-violet-100",
};

export default function StampeMensiliTab({
  token,
  apiBase = "/api/stampe-mensili",
  showIntro = true,
  /** Se passato, riceve il blocco mese/impostazioni da montare fuori (es. header OSS). */
  renderWithToolbar,
}) {
  const { activeStruttura } = useAuth();
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [cards, setCards] = useState([]);
  const [statiCount, setStatiCount] = useState({});
  const [meseLabel, setMeseLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardType, setWizardType] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [haccpOpen, setHaccpOpen] = useState(false);
  const [registriOpen, setRegistriOpen] = useState(false);
  const [statoFilter, setStatoFilter] = useState("tutti");

  const fetchCards = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}${apiBase}`, {
        params: { anno, mese },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCards(data.cards || []);
      setStatiCount(data.stati_count || {});
      setMeseLabel(data.mese_label || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento stampe mensili");
    } finally {
      setLoading(false);
    }
  }, [token, anno, mese, activeStruttura?.id, apiBase]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch al cambio struttura

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const shiftMonth = (delta) => {
    let m = mese + delta;
    let a = anno;
    if (m < 1) { m = 12; a -= 1; }
    if (m > 12) { m = 1; a += 1; }
    setMese(m);
    setAnno(a);
  };

  const handleCardAction = (card) => {
    if (card.print_type === "altri_registri") {
      setRegistriOpen(true);
      return;
    }
    if (card.print_type === "fogli_firma") {
      if (!card.modello_caricato) {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".pdf";
        input.onchange = async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const fd = new FormData();
          fd.append("file", file);
          try {
            await axios.post(`${API_URL}${apiBase}/fogli-firma/modello`, fd, {
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            });
            toast.success("Modello fogli firma caricato");
            fetchCards();
          } catch {
            toast.error("Errore upload modello");
          }
        };
        input.click();
      } else {
        openFogliFirmaPreview(card);
      }
      return;
    }
    if (["haccp", "haccp_pulizie", "schema_alvo", "schema_docce"].includes(card.print_type)) {
      setWizardType(card.print_type);
      setWizardOpen(true);
    }
  };

  const openPreview = (card) => {
    if (card.print_type === "fogli_firma") {
      openFogliFirmaPreview(card);
      return;
    }
    if (!card.file_id) {
      setWizardType(card.print_type);
      setWizardOpen(true);
      return;
    }
    setPreviewDoc({
      print_type: card.print_type,
      file_id: card.file_id,
      file_name: card.file_name,
      anno,
      mese,
    });
    setPreviewOpen(true);
  };

  const openFogliFirmaPreview = (card) => {
    if (!card.modello_caricato) {
      toast.info("Carica prima il modello PDF");
      return;
    }
    setPreviewDoc({
      print_type: "fogli_firma",
      file_name: card.file_name || `Fogli_Firma_${meseLabel}.pdf`.replace(/\s+/g, "_"),
      anno,
      mese,
      customUrl: `${API_URL}${apiBase}/fogli-firma/modello`,
    });
    setPreviewOpen(true);
  };

  const filtered = statoFilter === "tutti"
    ? cards
    : cards.filter((c) => c.stato === statoFilter);

  const monthToolbar = (
    <div className="flex items-center gap-2 flex-wrap sm:justify-end">
      <Button
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-xl bg-white/80"
        onClick={() => shiftMonth(-1)}
        aria-label="Mese precedente"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="min-w-[140px] text-center text-sm font-semibold text-slate-800">
        {meseLabel || "—"}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-xl bg-white/80"
        onClick={() => shiftMonth(1)}
        aria-label="Mese successivo"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        className="h-10 rounded-xl gap-2 bg-white/80"
        onClick={() => setHaccpOpen(true)}
      >
        <Settings className="h-4 w-4" />
        Impostazioni Stampe
      </Button>
    </div>
  );

  return (
    <>
      {typeof renderWithToolbar === "function" ? (
        renderWithToolbar(monthToolbar)
      ) : (
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {showIntro ? (
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600 shadow-sm ring-1 ring-orange-100">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Stampe Mensili</h2>
                <p className="text-sm text-slate-500">Documentazione periodica: HACCP, pulizie, schema alvo, docce e registri.</p>
              </div>
            </div>
          ) : (
            <div />
          )}
          {monthToolbar}
        </div>
      )}

      <div className="mb-5 flex flex-wrap gap-2">
        {[
          { key: "tutti", label: "Tutti", count: cards.length },
          { key: "da_stampare", label: "Da stampare", count: statiCount.da_stampare || 0 },
          { key: "stampato", label: "Stampato", count: statiCount.stampato || 0 },
          { key: "archiviato", label: "Archiviato", count: statiCount.archiviato || 0 },
          { key: "compilato", label: "Compilato", count: statiCount.compilato || 0 },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatoFilter(f.key)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${statoFilter === f.key ? "border-[#00A86B] bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#00A86B]" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {filtered.map((card) => {
            const Icon = ICONS[card.print_type] || Calendar;
            let meta = "";
            if (card.print_type === "haccp") meta = `${card.frighi_count || 0} frigo · ${card.congelatori_count || 0} congelatori`;
            if (card.print_type === "haccp_pulizie") meta = "Schede settimanali del mese";
            if (card.print_type === "schema_alvo" || card.print_type === "schema_docce") meta = `${card.ospiti_count || 0} ospiti`;
            if (card.print_type === "altri_registri") meta = `${card.registri_count || 0} registri configurati`;
            if (card.ultima_stampa) meta += meta ? ` · Ultima stampa ${new Date(card.ultima_stampa).toLocaleDateString("it-IT")}` : "";

            return (
              <DocumentCard
                key={card.id}
                icon={Icon}
                iconWrap={ICON_WRAP[card.print_type]}
                title={card.title}
                description={card.description}
                badge={
                  <Badge variant="outline" className={`text-[10px] ${STATO_COLORS[card.stato] || ""}`}>
                    {STATO_LABELS[card.stato] || card.stato}
                  </Badge>
                }
                meta={meta}
                primaryAction={
                  <Button
                    size="sm"
                    className="h-7 rounded-lg bg-[#00A86B] px-3 text-xs hover:bg-[#00915d]"
                    onClick={(e) => { e.stopPropagation(); handleCardAction(card); }}
                  >
                    {card.button_label}
                  </Button>
                }
                footerLeft={card.file_name ? "PDF generato" : "PDF"}
                footerRight={
                  card.file_id || (card.print_type === "fogli_firma" && card.modello_caricato) ? (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); openPreview(card); }}>
                      Anteprima
                    </Button>
                  ) : null
                }
                testId={`stampa-card-${card.print_type}`}
              />
            );
          })}
        </div>
      )}

      <GeneraMeseWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        printType={wizardType}
        anno={anno}
        mese={mese}
        meseLabel={meseLabel}
        token={token}
        apiBase={apiBase}
        onGenerated={(doc) => {
          fetchCards();
          setPreviewDoc(doc);
          setPreviewOpen(true);
        }}
      />

      <PrintPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        doc={previewDoc}
        token={token}
        apiBase={apiBase}
        customUrl={previewDoc?.customUrl}
        onPrinted={() => {
          if (previewDoc?.print_type) {
            axios.patch(`${API_URL}${apiBase}/${previewDoc.print_type}/stato`, null, {
              params: { anno: previewDoc.anno, mese: previewDoc.mese, stato: "stampato" },
              headers: { Authorization: `Bearer ${token}` },
            }).then(() => fetchCards());
          }
        }}
      />

      <HaccpConfigDialog
        open={haccpOpen}
        onOpenChange={setHaccpOpen}
        token={token}
        apiBase={apiBase}
        onSaved={fetchCards}
      />
      <RegistriDialog open={registriOpen} onOpenChange={setRegistriOpen} token={token} apiBase={apiBase} />
    </>
  );
}
